/**
 * TenantContext Provider
 * 
 * Provides tenant context to all child components.
 * Handles tenant detection, loading, and error states.
 * 
 * Created: 2024-12-19
 * Task: 4.1 - Create TenantContext
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import type { TenantConfig } from '@bizora/types'
import { getTenantSlug, initializeTenant } from '../tenant'
import { fetchTenantBySlug } from '../db'

export interface Tenant {
  id: string
  slug: string
  name: string
  email?: string
  status: string
  vertical: string
  [key: string]: any // Allow additional tenant properties
}

export interface TenantContextValue {
  // Tenant data
  tenant: Tenant | null
  config: TenantConfig | null
  slug: string | null
  
  // State
  loading: boolean
  error: Error | null
  
  // Actions
  refresh: () => Promise<void>
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined)

interface TenantProviderProps {
  children: React.ReactNode
  /** Optional tenant slug override (for testing or explicit tenant selection) */
  tenantSlug?: string
}

/**
 * TenantProvider Component
 * 
 * Provides tenant context to all child components.
 * Automatically detects tenant from URL and loads tenant data.
 */
export function TenantProvider({ children, tenantSlug: explicitSlug }: TenantProviderProps) {
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [config, setConfig] = useState<TenantConfig | null>(null)
  const [slug, setSlug] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Load tenant data and config
   */
  const loadTenant = useCallback(async (tenantSlug: string) => {
    setLoading(true)
    setError(null)

    try {
      // Detect tenant slug if not provided
      const detectedSlug = tenantSlug || getTenantSlug()
      setSlug(detectedSlug)

      // Fetch tenant from database
      const tenantData = await fetchTenantBySlug(detectedSlug)
      
      if (!tenantData) {
        throw new Error(`Tenant "${detectedSlug}" not found`)
      }

      // Set tenant data
      setTenant(tenantData as Tenant)

      // Initialize tenant config (loads from JSON or generates from DB)
      const tenantConfig = await initializeTenant(detectedSlug)
      setConfig(tenantConfig)

      setLoading(false)
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      setLoading(false)
      
      // Log error in development
      if (import.meta.env.DEV) {
        console.error('[TenantProvider] Failed to load tenant:', error.message)
      }
    }
  }, [])

  /**
   * Refresh tenant data
   */
  const refresh = useCallback(async () => {
    if (slug) {
      await loadTenant(slug)
    }
  }, [slug, loadTenant])

  // Load tenant on mount or when explicit slug changes
  useEffect(() => {
    const tenantSlug = explicitSlug || getTenantSlug()
    loadTenant(tenantSlug)
  }, [explicitSlug, loadTenant])

  // Listen for URL changes (for SPA navigation)
  useEffect(() => {
    const handleLocationChange = () => {
      const newSlug = getTenantSlug()
      if (newSlug !== slug) {
        loadTenant(newSlug)
      }
    }

    // Listen to popstate (back/forward navigation)
    window.addEventListener('popstate', handleLocationChange)
    
    // Also check on pathname changes (for programmatic navigation)
    // Note: This is a simple approach; for more complex routing, consider using React Router's location
    const interval = setInterval(() => {
      const newSlug = getTenantSlug()
      if (newSlug !== slug) {
        handleLocationChange()
      }
    }, 1000) // Check every second

    return () => {
      window.removeEventListener('popstate', handleLocationChange)
      clearInterval(interval)
    }
  }, [slug, loadTenant])

  const value: TenantContextValue = {
    tenant,
    config,
    slug,
    loading,
    error,
    refresh
  }

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  )
}

/**
 * Hook to access tenant context
 * 
 * @throws Error if used outside TenantProvider
 */
export function useTenantContext(): TenantContextValue {
  const context = useContext(TenantContext)
  
  if (context === undefined) {
    throw new Error('useTenantContext must be used within a TenantProvider')
  }
  
  return context
}
